import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, AlertTriangle, CheckCircle2, Undo2, ArrowLeft, FileSpreadsheet } from "lucide-react";
import { fmtDate } from "@/lib/dates";

export default function SchoolImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [batches, setBatches] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const fileRef = useRef(null);

  const loadBatches = () => api.get("/admin/school-import/batches").then(({ data }) => setBatches(data)).catch(() => {});
  useEffect(() => { loadBatches(); }, []);

  const send = async (path, setter, label) => {
    if (!file) return toast.error("Choose the spreadsheet first.");
    setBusy(label);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(data);
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setResult(null);
  };

  const doPreview = () => send("/admin/school-import/preview", setPreview, "preview");
  const doImport = async () => {
    const data = await send("/admin/school-import/commit", setResult, "import");
    if (data) { toast.success(`${data.created} schools created, ${data.updated} updated`); setPreview(null); loadBatches(); }
  };

  const undo = async (b) => {
    setBusy(b.id);
    try {
      const { data } = await api.post(`/admin/school-import/batches/${b.id}/undo`);
      toast.success(`Undone — ${data.removed} schools removed`);
      setResult(null); loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const stats = preview?.stats || result?.stats;
  const issues = preview?.issues || result?.issues || [];

  return (
    <div data-testid="school-import-page">
      <Link to="/schools" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-cc-blue mb-3" data-testid="back-to-schools">
        <ArrowLeft className="w-4 h-4" />Back to Schools
      </Link>
      <PageHeader title="Import schools" subtitle="Upload your spreadsheet, check what it will do, then import. Nothing is emailed and no accounts are created — contacts are saved as records you can invite later." />

      <Card title="Spreadsheet" data-testid="import-upload-card">
        <p className="text-sm text-zinc-500 mb-3">
          One row per school and program, with columns: school name, program, program director name/email/phone,
          student rotation contact name/email/phone, campus city, state. Rows for the same campus are combined into one school.
        </p>
        <input type="file" accept=".xlsx,.xlsm" className="hidden" ref={fileRef} onChange={onPick} data-testid="import-file-input" />
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-choose-file">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />{file ? "Choose a different file" : "Choose spreadsheet"}
          </Button>
          {file && <span className="text-sm text-zinc-600" data-testid="import-file-name">{file.name}</span>}
          {file && (
            <Button onClick={doPreview} disabled={!!busy} className="bg-cc-blue text-white" data-testid="import-preview-btn">
              {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview import"}
            </Button>
          )}
        </div>
      </Card>

      {stats && (
        <Card title={result ? "Imported" : "Preview"} className="mt-6" data-testid="import-summary-card"
          action={result ? <span className="text-sm text-cc-green inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Done</span> : null}>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            {[["Rows read", stats.rows_read], ["Schools", stats.schools], ["Programs", stats.programs],
              ["Contacts", stats.contacts], ["States", stats.states]].map(([k, v]) => (
              <div key={k} className="rounded-md bg-zinc-50 py-3">
                <div className="text-2xl font-semibold text-zinc-800" data-testid={`stat-${k.toLowerCase().replace(" ", "-")}`}>{v}</div>
                <div className="text-xs uppercase tracking-wide text-zinc-400">{k}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <span className="text-cc-green font-medium" data-testid="import-create-count">{result ? result.created : stats.to_create} to create</span>
            <span className="text-cc-blue font-medium" data-testid="import-update-count">{result ? result.updated : stats.to_update} existing to update</span>
            {issues.length > 0 && (
              <span className="text-cc-orange font-medium inline-flex items-center gap-1" data-testid="import-issue-count">
                <AlertTriangle className="w-4 h-4" />{issues.length} rows need a look
              </span>
            )}
          </div>
          {preview && (
            <div className="mt-5 flex items-center gap-3">
              <Button onClick={doImport} disabled={!!busy} className="bg-cc-green hover:bg-green-600 text-white" data-testid="import-commit-btn">
                {busy === "import" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" />Import {stats.schools} schools</>}
              </Button>
              <span className="text-xs text-zinc-500">You can undo this afterwards.</span>
            </div>
          )}
        </Card>
      )}

      {preview?.schools?.length > 0 && (
        <Card title="What will be created" className="mt-6 p-0 overflow-hidden" data-testid="import-plan-card"
          action={<button className="text-sm text-cc-blue" onClick={() => setShowAll((v) => !v)} data-testid="import-toggle-all">
            {showAll ? "Show first 25" : `Show all ${preview.schools.length}`}</button>}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-400">
                <tr><th className="px-4 py-2">School</th><th className="px-4 py-2">City</th><th className="px-4 py-2">State</th>
                  <th className="px-4 py-2">Programs</th><th className="px-4 py-2">Contacts</th><th className="px-4 py-2">Action</th></tr>
              </thead>
              <tbody className="divide-y">
                {(showAll ? preview.schools : preview.schools.slice(0, 25)).map((s, i) => (
                  <tr key={`${s.name}-${i}`} data-testid={`import-row-${i}`}>
                    <td className="px-4 py-2 text-zinc-800">{s.name}</td>
                    <td className="px-4 py-2 text-zinc-500">{s.city || "—"}</td>
                    <td className="px-4 py-2 text-zinc-500">{s.state}</td>
                    <td className="px-4 py-2 text-zinc-600">{s.programs.join(", ") || "—"}</td>
                    <td className="px-4 py-2 text-zinc-600">{s.contact_count}</td>
                    <td className="px-4 py-2">
                      <span className={s.action === "create" ? "text-cc-green" : "text-cc-blue"}>{s.action === "create" ? "Create" : "Update existing"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {issues.length > 0 && (
        <Card title="Rows that need a look" className="mt-6" data-testid="import-issues-card">
          <p className="text-sm text-zinc-500 mb-3">These rows were still imported where possible — this is just so you know what was odd.</p>
          <ul className="space-y-1 text-sm max-h-72 overflow-y-auto">
            {issues.map((it, i) => (
              <li key={i} className="text-zinc-600" data-testid={`import-issue-${i}`}>
                <span className="text-zinc-400">Row {it.row}:</span> {it.issue}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Import history" className="mt-6" data-testid="import-history-card">
        {batches.length === 0 ? <EmptyState text="No imports yet." /> : (
          <div className="divide-y">
            {batches.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`import-batch-${b.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-zinc-800">{b.file_name}</p>
                  <p className="text-xs text-zinc-400">
                    {fmtDate(b.imported_at)} by {b.imported_by_name} · {b.stats?.created || 0} created, {b.stats?.updated || 0} updated
                    {b.undone_at ? " · undone" : ""}
                  </p>
                </div>
                {!b.undone_at && (
                  <Button variant="outline" size="sm" className="text-red-600" disabled={busy === b.id}
                    onClick={() => undo(b)} data-testid={`undo-batch-${b.id}`}>
                    {busy === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Undo2 className="w-4 h-4 mr-1.5" />Undo</>}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
